import { CheckOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Radio } from "antd";
import moment from "moment";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  KEY_SHOPPING_CART,
  OTP_ERROR_CODES_PREVENT_CREATE_OERDER,
  OTP_ERROR_MESSAGES,
  OTP_METHOD,
  VERIFY_OTP_ERROR_CODES,
} from "../../common/constant";
import { IShoppingCart } from "../../models/IShoppingCartModel";
import { getVerifyOTPInfo } from "../../services/shoppingCart";
import { markPhoneNumber } from "../../shared/helpers";
import NotifyOTPFail from "./notifyOTPFail";
import Timer from "./timer";
import Turnstile from "react-turnstile";
import { CLOUDFLARE_SITE_KEY } from "../../utils/constant";

const CODE_LENGTH = 6;

const STEP_SELECT_METHOD = "SELECT_METHOD";
const STEP_VERIFY_OTP = "VERIFY_OTP";

export interface IOTPInfo {
  code?: string;
  otp?: string;
  data?: any;
  message?: string;
  expire_at?: string;
  id?: string;
  otp_method?: string;
  is_send_zalo_success?: boolean;
}

const VerifyOTPModal = (props: any, ref: React.Ref<unknown> | undefined) => {
  const { gotoComplete } = props;

  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(STEP_SELECT_METHOD);
  const [otpMethod, setOtpMethod] = useState(OTP_METHOD.ZALO);
  const [code, setCode] = useState("");
  const [resendOTP, setResendOTP] = useState(false);
  const [resendOTPSuccess, setResendOTPSuccess] = useState(false);
  const [verifyOTPInfo, setVerifyOTPInfo] = useState<IOTPInfo | null>(null);
  const [cartInfo, setCartInfo] = useState<{ [key: string]: any }>({});
  const [showCaptcha, setShowCaptcha] = useState(false);

  const carts = localStorage.getItem(KEY_SHOPPING_CART);
  let shoppingCart: IShoppingCart = carts ? JSON.parse(carts) : null;

  let parseOTPInfo = getVerifyOTPInfo(
    shoppingCart?.shipping_info?.phone_number
  );

  const expire_at = verifyOTPInfo?.expire_at || parseOTPInfo?.expire_at;

  const now = moment().toISOString();

  const markPhone = markPhoneNumber(
    shoppingCart?.shipping_info?.phone_number,
    "******"
  );

  useImperativeHandle(ref, () => {
    return {
      openModal,
      handleVerifyOTP,
      closeModal,
    };
  });

  const handleVerifyOTP = (error: IOTPInfo) => {
    setVerifyOTPInfo(error);
    if (error?.expire_at || error?.id) {
      setStep(STEP_VERIFY_OTP);
    }
  };

  const openModal = (info: IOTPInfo, cartInfo = {}) => {
    setVerifyOTPInfo(info);
    setCartInfo(cartInfo);
    setResendOTP(false);
    setOtpMethod(info?.otp_method || OTP_METHOD.ZALO);

    if (info?.expire_at && info?.id) {
      setStep(STEP_VERIFY_OTP);
    } else {
      setStep(STEP_SELECT_METHOD);
    }

    setVisible(true);
  };

  const closeModal = () => {
    setCode("");
    setVerifyOTPInfo(null);
    setCartInfo({});
    setResendOTPSuccess(false);
    setResendOTP(false);
    setShowCaptcha(false);
    setStep(STEP_SELECT_METHOD);
    setVisible(false);
  };

  const calcExpireTime = () => {
    const diffTime = moment(expire_at).diff(now, "s");

    if (diffTime <= 0) {
      return null;
    }
    return diffTime;
  };

  const onSelectMethodContinue = () => {
    gotoComplete({
      ...cartInfo,
      otp_method: otpMethod,
      otp_verify: { otp: null, expire_at: null, id: null },
    });
  };

  const onComplete = () => {
    gotoComplete({ otp: code, otp_method: otpMethod });
    setResendOTPSuccess(false);
    setResendOTP(false);
  };

  const onResendOTP = () => {
    setShowCaptcha(true);
  };

  const handleResendCaptchaSuccess = (token: string) => {
    setShowCaptcha(false);
    setVerifyOTPInfo(null);
    gotoComplete({
      otp_verify: { otp: null, expire_at: null, id: null },
      token,
    });
    setResendOTP(false);
    setResendOTPSuccess(true);
  };

  const onTimeout = (result = false) => {
    if (result) {
      setResendOTP(result);
    }
  };

  const checkError = () =>
    verifyOTPInfo &&
    verifyOTPInfo.code &&
    verifyOTPInfo.code != VERIFY_OTP_ERROR_CODES.VERIFY_OTP_NOT_EXPIRED &&
    verifyOTPInfo.message;

  const showErrorMessage = () =>
    OTP_ERROR_MESSAGES[verifyOTPInfo?.code || ""] || verifyOTPInfo?.message;

  const renderTurnstile = () => (
    <div
      style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}>
      <Turnstile
        sitekey={`${CLOUDFLARE_SITE_KEY}`}
        onSuccess={handleResendCaptchaSuccess}
        onError={() => setShowCaptcha(false)}
        onExpire={() => setShowCaptcha(false)}
        onTimeout={() => setShowCaptcha(false)}
        theme="light"
      />
    </div>
  );

  const renderSelectMethod = () => (
    <>
      <div className="verify-otp__title">Chọn phương thức nhận mã OTP</div>
      <div className="verify-otp__des">
        <div>
          Vui lòng chọn phương thức nhận mã xác nhận (OTP) cho số điện thoại{" "}
          {markPhone}
        </div>
      </div>
      <div className="verify-otp__method">
        <Radio.Group
          value={otpMethod}
          onChange={(e) => setOtpMethod(e.target.value)}>
          <Radio value={OTP_METHOD.ZALO}>Gửi qua Zalo</Radio>
          <Radio value={OTP_METHOD.SMS}>Gửi qua SMS</Radio>
        </Radio.Group>
      </div>
      <div className="verify-otp__btn">
        <Button
          style={{ lineHeight: 1 }}
          className="kd-btn-primary-2 main-content"
          onClick={onSelectMethodContinue}>
          Tiếp tục
        </Button>
      </div>
    </>
  );

  const renderVerifyOTP = () => (
    <>
      <div className="verify-otp__title">Nhập mã xác nhận (OTP)</div>
      <div className="verify-otp__des">
        <div>
          {otpMethod === OTP_METHOD.ZALO && !verifyOTPInfo?.is_send_zalo_success
            ? `Mã xác nhận (OTP) gửi qua Zalo không thành công, hệ thống đã gửi SMS đến số điện thoại ${markPhone}`
            : `Mã xác nhận (OTP) đã được gửi đến Quý khách qua ${
                otpMethod === OTP_METHOD.SMS ? "tin nhắn SMS" : "Zalo"
              } đến số điện thoại ${markPhone}`}
        </div>
        <div>
          Quý khách vui lòng nhập mã vào khung bên dưới để hoàn tất đặt hàng.
        </div>
      </div>
      <div
        className={`verify-otp__input ${
          checkError() && "verify-otp__input-fail"
        }`}>
        <Input
          suffix={checkError() && <ExclamationCircleOutlined />}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Code"
          maxLength={CODE_LENGTH}
        />
      </div>
      <div className="verify-otp__noti">
        {checkError() && <div className="noti-left">{showErrorMessage()}</div>}
        {calcExpireTime() && resendOTPSuccess ? (
          <div className="noti-left noti-success">
            <CheckOutlined style={{ color: "#05c46b" }} /> Mã xác nhận (OTP) đã
            được gửi lại
          </div>
        ) : null}
        {calcExpireTime() && !resendOTP ? (
          <div className="noti-right">
            Yêu cầu gửi lại mã xác nhận (OTP) sau{" "}
            <Timer
              delayResend={calcExpireTime()}
              onTimeout={onTimeout}
              phoneNumber={shoppingCart?.shipping_info?.phone_number}
            />
          </div>
        ) : (
          (resendOTP ||
            (expire_at && !calcExpireTime()) ||
            VERIFY_OTP_ERROR_CODES[verifyOTPInfo?.code || ""]) && (
            <>
              {showCaptcha && renderTurnstile()}
              {!showCaptcha && (
                <a onClick={onResendOTP} className="noti-right">
                  Yêu cầu gửi lại mã xác nhận (OTP)
                </a>
              )}
            </>
          )
        )}
      </div>
      <div className="verify-otp__btn">
        <Button
          style={{ lineHeight: 1 }}
          className="kd-btn-primary-2 main-content"
          onClick={onComplete}
          disabled={code.length != CODE_LENGTH}>
          Hoàn tất
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      className="kd-verify-otp-modal"
      width={700}
      visible={visible}
      onCancel={closeModal}
      footer={null}>
      {OTP_ERROR_CODES_PREVENT_CREATE_OERDER.includes(verifyOTPInfo?.code) ? (
        <NotifyOTPFail
          errorCode={verifyOTPInfo?.code || ""}
          markPhone={markPhone}
          closeModal={closeModal}
        />
      ) : step === STEP_SELECT_METHOD ? (
        renderSelectMethod()
      ) : (
        renderVerifyOTP()
      )}
    </Modal>
  );
};

export default forwardRef(VerifyOTPModal);
